/**
 * Centralized story insights pulled from developer-story.org.
 * Both modern and terminal themes consume this single source.
 */

export interface StoryInsight {
  quote: string
  attribution?: string
}

export const storyInsights: Record<string, StoryInsight[]> = {
  about: [
    {
      quote:
        'Before I ever wrote my first line of code, I spent nearly a decade at a ' +
        'farm-based Montessori school. I learned that people learn best when ' +
        "they're intrinsically motivated, when the work emerges from genuine " +
        'curiosity, and when different working styles are respected rather than ' +
        'flattened into uniformity.',
      attribution: 'Prairie Hill Learning Center, 2007-2015',
    },
  ],
  hero: [
    {
      quote:
        "The most powerful use of technology isn't replacing human thinking. " +
        "It's amplifying our natural problem-solving processes.",
    },
  ],
  contact: [
    {
      quote:
        'I thrive in technology-focused teams that respect how people actually work best. ' +
        "I'm completely technology-agnostic; I love learning new languages and frameworks. " +
        'What really matters is working with people who have genuine respect for different ' +
        'working styles.',
    },
  ],
  story: [
    {
      quote:
        "There's a thread connecting all of it. From watching toddlers discover how the " +
        'world works, to building payment infrastructure, to automating away tedious ' +
        "processes, to giving a rubber duck a brain. The thread is this: the most " +
        "powerful use of technology isn't replacing human thinking. It's amplifying " +
        'our natural problem-solving processes.',
    },
  ],
  philosophy: [
    {
      quote:
        'The real skill becomes knowing what problems to solve and how to validate ' +
        'that your solution actually works. Testing, monitoring, security practices, ' +
        'deployment strategies; these engineering fundamentals become more important, ' +
        'not less.',
      attribution: 'On AI-assisted development',
    },
  ],
  epiphany: [
    {
      quote:
        'I was building a portfolio site after being laid off and struggling to write ' +
        'technical articles. Then I remembered working with clay in the Montessori ' +
        'classroom. Just like clay, I could start with raw material and shape it later. ' +
        'I built a transcription tool so I could speak freely and use the transcript ' +
        'as my "clay of words." Then came the lightbulb: what if I fed the transcript ' +
        'into an LLM?',
      attribution: 'The moment can.code began, 2024',
    },
  ],
}

export const storyChapters = [
  { label: 'Montessori', period: '2007-2015' },
  { label: 'Payments', period: '2016-2019' },
  { label: 'Tax', period: '2019-2022' },
  { label: 'Security', period: '2022-2024' },
  { label: 'AI & can.code', period: '2024-Present' },
]

export const lookingFor = [
  'Technology-focused teams that respect how people actually work best',
  'Complex technical problems requiring deep thinking and systematic solutions',
  'Genuine collaboration between engineering and leadership',
  'Environments where technical judgment is trusted',
]
